import React, { useState, useEffect } from 'react';
import InfoAddModal from './InfoAddModal';
import { useToast } from '@/contexts/ToastContext';
import { AISHA_API_BASE } from '@/constants';
import { 
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '../icons/HeroIcons';

interface InformationItem {
  id: number;
  date: string;
  title: string;
  type: 'notice' | 'menu_add' | 'error';
  type_display: string;
  content: {
    message?: string;
    category?: string;
    menu_name?: string;
    error_message?: string;
    error_time?: string;
    fix_time?: string;
  };
  created_at: string;
  updated_at: string;
}

interface InformationListResponse {
  success: boolean;
  items: InformationItem[];
  total: number;
  page: number;
  total_pages: number;
  per_page: number;
  is_admin: boolean;
}

const InformationManagement: React.FC = () => {
  const { showToast } = useToast();
  const [informationList, setInformationList] = useState<InformationItem[]>([]);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InformationItem | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchInformation = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '20',
        user_id: localStorage.getItem('userId') || ''
      });
      
      if (selectedType !== 'all') {
        params.append('type', selectedType);
      }

      const response = await fetch(`${AISHA_API_BASE}/admin/information/?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data: InformationListResponse = await response.json();

      if (data.success) {
        setInformationList(data.items);
        setTotalPages(data.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch information:', error);
      showToast('お知らせ一覧の取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInformation();
  }, [currentPage, selectedType]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('このお知らせを削除しますか？')) {
      return;
    }

    try {
      const params = new URLSearchParams({
        user_id: localStorage.getItem('userId') || ''
      });

      const response = await fetch(`${AISHA_API_BASE}/admin/information/${id}/delete/?${params.toString()}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        showToast('お知らせを削除しました', 'success');
        fetchInformation();
      }
    } catch (error) {
      console.error('Failed to delete information:', error);
      showToast('削除に失敗しました', 'error');
    }
  };

  const handleEdit = (item: InformationItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    fetchInformation();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'notice':
        return <InformationCircleIcon className="w-4 h-4" />;
      case 'menu_add':
        return <PlusCircleIcon className="w-4 h-4" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'notice':
        return 'bg-blue-100 text-blue-800';
      case 'menu_add':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatContent = (item: InformationItem) => {
    const { type, content } = item;
    
    if (type === 'notice') {
      return content.message || '';
    } else if (type === 'menu_add') {
      return `カテゴリ: ${content.category || '-'} / メニュー: ${content.menu_name || '-'}`;
    } else if (type === 'error') {
      let text = content.error_message || '';
      if (content.error_time) {
        text += ` (発生: ${content.error_time})`;
      }
      if (content.fix_time) {
        text += ` (修正: ${content.fix_time})`;
      }
      return text;
    }
    return '';
  };

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h3 className="text-xl font-semibold text-indigo-300">お知らせ管理</h3>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-lg transition-colors"
        >
          <PlusCircleIcon className="w-5 h-5" />
          新規作成
        </button>
      </div>

      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setSelectedType('all')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            selectedType === 'all' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          すべて
        </button>
        <button
          onClick={() => setSelectedType('notice')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            selectedType === 'notice' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          お知らせ
        </button>
        <button
          onClick={() => setSelectedType('menu_add')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            selectedType === 'menu_add' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          メニュー追加
        </button>
        <button
          onClick={() => setSelectedType('error')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            selectedType === 'error' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          エラー情報
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    日付
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    種別
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    タイトル
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    内容
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {informationList.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-gray-500" />
                        {item.date}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
                        {getTypeIcon(item.type)}
                        {item.type_display}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">
                      {item.title}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      <div className="max-w-md truncate">
                        {formatContent(item)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-400 hover:text-blue-300 p-1"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg transition-colors ${
                    page === currentPage
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {isModalOpen && (
        <InfoAddModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          editingItem={editingItem}
        />
      )}
    </div>
  );
};

export default InformationManagement;